import React, { forwardRef, useId } from "react";
import {
  FieldLabel,
  FieldRoot,
  HelperText,
  LabelRow,
  RequiredMark,
  StyledTextArea,
} from "./TextAreaField.styles";
import type { TextAreaFieldProps } from "./TextAreaField.types";

export const TextAreaField = forwardRef<HTMLTextAreaElement, TextAreaFieldProps>(
  (
    {
      label,
      required,
      helperText,
      errorMessage,
      id,
      className,
      rows = 6,
      labelAction,
      ...textAreaProps
    },
    ref,
  ) => {
    const fallbackId = useId();
    const fieldId = id ?? `textareafield-${fallbackId}`;
    const helperId = `${fieldId}-helper`;
    const hasError = Boolean(errorMessage);
    const labelElement = (
      <FieldLabel htmlFor={fieldId} $surface="config">
        {label}
        {required ? <RequiredMark aria-hidden="true">*</RequiredMark> : null}
      </FieldLabel>
    );

    return (
      <FieldRoot className={className} $surface="config">
        {labelAction ? (
          <LabelRow>
            {labelElement}
            {labelAction}
          </LabelRow>
        ) : (
          labelElement
        )}
        <StyledTextArea
          ref={ref}
          id={fieldId}
          rows={rows}
          $invalid={hasError}
          aria-required={required}
          aria-invalid={hasError || undefined}
          aria-describedby={helperText || errorMessage ? helperId : undefined}
          {...textAreaProps}
        />
        {(errorMessage || helperText) && (
          <HelperText id={helperId} $error={hasError} $surface="config" role={hasError ? "alert" : undefined}>
            {errorMessage || helperText}
          </HelperText>
        )}
      </FieldRoot>
    );
  },
);

TextAreaField.displayName = "TextAreaField";
